import { useCallback, useEffect, useRef, useState } from "react";

// Keeps one gateway snapshot fresh while a screen shows it. Screens only see
// { status, data, error, stale, retry }, so a realtime transport can later
// replace the timer here without touching them.

export const TEACHING_MS = 5000;
export const WORKSPACE_MS = 15000;

// After consecutive failures: 5s, 10s, 20s, then every 30s until it works.
export const BACKOFF_MS = [5000, 10000, 20000, 30000];
export const backoffDelay = (failures) =>
  BACKOFF_MS[Math.min(Math.max(failures, 1), BACKOFF_MS.length) - 1];

// Refusals that asking again cannot fix; only a manual retry tries again.
const isFinal = (error) => [400, 401, 403, 404].includes(error?.response?.status);

// A teacher's own change (a review, an observation) refreshes every visible
// snapshot straight away instead of waiting for the next poll.
const listeners = new Set();
export const announceWorkspaceChange = () => listeners.forEach((listener) => listener());

const initial = (key, enabled) => ({
  key,
  status: enabled ? "loading" : "unavailable",
  data: null,
  error: null,
  failures: 0,
});

const usePolledResource = (
  load,
  key,
  { intervalMs = WORKSPACE_MS, enabled = true } = {}
) => {
  const [state, setState] = useState(() => initial(key, enabled));
  const [version, setVersion] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let timer = null;
    let inFlight = false;
    let stopped = false;
    let failures = 0;

    const schedule = (ms) => {
      clearTimeout(timer);
      timer = setTimeout(tick, ms);
    };

    async function tick() {
      clearTimeout(timer);
      timer = null;
      // A hidden screen (locked tablet, another app) does not poll; it picks
      // up again when it is shown.
      if (inFlight || stopped || document.hidden) return;
      inFlight = true;
      try {
        const data = await loadRef.current();
        if (!active) return;
        failures = 0;
        setState({ key, status: "ready", data, error: null, failures: 0 });
        schedule(intervalMs);
      } catch (error) {
        if (!active) return;
        failures += 1;
        setState((previous) => ({
          ...(previous.key === key ? previous : initial(key, true)),
          status: "error",
          error,
          failures,
        }));
        if (isFinal(error)) stopped = true;
        else schedule(backoffDelay(failures));
      } finally {
        inFlight = false;
      }
    }

    const now = () => {
      if (active && !stopped) tick();
    };
    const onVisible = () => {
      if (!document.hidden && timer === null) now();
    };

    tick();
    listeners.add(now);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", now);
    return () => {
      active = false;
      clearTimeout(timer);
      listeners.delete(now);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", now);
    };
  }, [key, enabled, intervalMs, version]);

  // Asks again now and starts the normal rhythm over, even after a refusal.
  const retry = useCallback(() => {
    setState((previous) =>
      previous.data ? previous : { ...previous, status: "loading", error: null }
    );
    setVersion((value) => value + 1);
  }, []);

  const current = state.key === key ? state : initial(key, enabled);
  return {
    ...current,
    status: enabled ? current.status : "unavailable",
    stale: current.status === "error" && current.data != null,
    retry,
  };
};

export default usePolledResource;
